import React, { useContext, useEffect, useState, useRef } from 'react';
import { HoverContext } from './context';
import { demoTooltipTable } from './utils/demoTable';
import { select, selectAll } from 'd3-selection';
import { object, bool } from 'prop-types';

const ToolTip = ( { tooltipProps, isPercent } ) => {

    // can probably simplify this list, this was a big overhaul
    // it works as is currently
    const { hover: { xVal, yVal, d, lines } } = useContext( HoverContext );
    const { label, isLVP, data, yEndVal, chartNo, getX, getY, widthForScale, margin } = tooltipProps;
    const tooltipRef = useRef( );
    const [ tooltipWidth, setTooltipWidth ] = useState( 0 );

    useEffect( () => {

        if ( tooltipRef.current !== undefined && xVal !== undefined ) {

            setTooltipWidth( tooltipRef.current.getBoundingClientRect().width );

        }

    }, [ xVal ] );

    const tooltipData = data.find( ( datapoint ) => {

        if ( d ) {

            const [ year, month ] = d;

            return datapoint.column.date.getFullYear() === year && datapoint.column.date.getMonth() === month;

        }

        return undefined;

    } );

    const updateHoverDots = () => {

        selectAll( `.${ chartNo } .dot-per-line` )
            .attr( 'transform', ( ( _, i ) => {

                if ( !tooltipData ) return 'translate(0,0)';

                const y2 = getY( tooltipData ? tooltipData.rows[i].value : 0 );

                const x2 = getX( new Date( d[0], d[1] ) );

                return `translate(${ x2 || 0 },${ y2 || 0 })`;

            } ) )
            .attr( 'fill', '#ffffff' )
            .attr( 'stroke', ( _, i ) => ( lines ? lines[i].getAttribute( 'stroke' ) : 'none' ) )
            .attr( 'opacity', ( () => ( tooltipData ? 1 : 0 ) ) );

    };

    const updateScrubberLine = () => {

        // Move scrubber line
        select( `.${ chartNo } .scrubber` )
            .attr( 'd', () => `M${ xVal || 0 },0 ${ xVal || 0 },${ yEndVal }` )
            .attr( 'opacity', () => ( tooltipData ? 1 : 0 ) );

    };

    const createTooltipStyles = () => {

        const tooltipOffset = 16;
        const style = { top: `${ yEndVal - ( yVal * -1 ) - 180 }px`, left: '' };
        const centerChart = widthForScale / 2;
        const mobileMiddleAlignZone = 50;
        const startTooltip = xVal + margin.left;

        if ( isLVP ) {

            style['left'] = xVal <= centerChart ? `${ startTooltip + tooltipOffset }px` : `${ startTooltip - tooltipWidth - tooltipOffset }px`;

        }
        else {

            style['left'] = xVal <= centerChart - mobileMiddleAlignZone
                ? `${ startTooltip + tooltipOffset }px`
                : xVal <= centerChart + mobileMiddleAlignZone
                    ? `${ startTooltip - ( tooltipWidth / 2 ) }px`
                    : `${ startTooltip - tooltipWidth - tooltipOffset }px`;

        }

        return style;

    };

    const renderMultilineTooltip = () => {

        return tooltipData.rows.map( ( line, i ) => {

            let formattedLineValue = '';

            if ( line.value && !isPercent ) {

                // Is a number in the thousands
                // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat
                const formatter = Intl.NumberFormat( 'en', { notation: 'compact', minimumFractionDigits: 1, maximumFractionDigits: 1 } );

                formattedLineValue = line.value ? `$${ formatter.format( line.value ).toLowerCase()
                    .split( 'k' )[0] }K` : line.value === 0 ? '0k' : 'N/A';

            }
            else {

                // is a percentage
                formattedLineValue = line.value ? `${ line.value.toFixed( 1 ) }%` : line.value === 0 ? '0.0%' : 'N/A';

            }

            let k = '';

            const [ region ] = label.find( ( l ) => l[1] === line.color );

            for ( const [ key, value ] of Object.entries( demoTooltipTable ) ) {

                if ( line['name'].includes( key ) ) {

                    k = value;
                    break;

                }
                else {

                    k = 'Error...';

                }

            }

            return (
                <tr key={ `line${ i }` }>
                    <td className='color' style={{ backgroundColor: line.color ? line.color : 'none' }}></td>
                    { isLVP && <td className='label'> { `${ k }: ${ region }` } </td> }
                    <td className='value'> { formattedLineValue }</td>
                </tr>
            );

        } );

    };

    const getDate = () => {

        const qtr = Math.floor( ( tooltipData.column.date.getMonth() + 3 ) / 3 );
        const date = `${ tooltipData.column.date.getFullYear() } Q${ qtr }`;

        return date;

    };


    updateHoverDots();
    updateScrubberLine();

    return (
        tooltipData
            ? <div className='tooltip' style={ createTooltipStyles() } ref= { tooltipRef }>
                <h2> { getDate() } </h2>
                <table>
                    <tbody>
                        { renderMultilineTooltip() }
                    </tbody>
                </table>
            </div>
            : null
    );

};

ToolTip.propTypes = {
    tooltipProps: object.isRequired,
    isPercent: bool.isRequired
};

export default ToolTip;
